import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import {
  LocationPicker,
  type LocationValue,
} from "@/components/ui/LocationPicker";
import { useAuth } from "@/context/AuthContext";
import { useThemeColor } from "@/hooks/useThemeColor";
import firestore, { GeoPoint } from "@react-native-firebase/firestore";
import storage from "@react-native-firebase/storage";
import * as ImagePicker from "expo-image-picker";
import { useRouter } from "expo-router";

import { Image } from "expo-image";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Switch,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
const DefaultGroupImage = require("../../../assets/images/default-group.png");

export default function CreateGroupScreen() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();
  const iconColor = useThemeColor({}, "icon");
  const [name, setName] = useState("");
  const [groupImgUri, setGroupImgUri] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [location, setLocation] = useState<LocationValue>(null);
  const [allowMemberLocationOverride, setAllowMemberLocationOverride] =
    useState(false);
  const [isPublic, setIsPublic] = useState(true);

  const handleCreateGroup = async () => {
    if (!user) {
      Alert.alert(t("ERROR"), t("YOU_MUST_BE_LOGGED_IN"));
      return;
    }

    if (!name.trim()) {
      Alert.alert(t("ERROR"), t("GROUP_NAME_REQUIRED"));
      return;
    }

    setLoading(true);
    try {
      const baseData: Record<string, unknown> = {
        name,
        description,
        adminId: user.uid,
        memberIds: [user.uid],
        createdAt: firestore.FieldValue.serverTimestamp(),
        visibility: isPublic ? "public" : "private",
      };
      if (location) {
        baseData.location = {
          geoPoint: new GeoPoint(location.latitude, location.longitude),
          city: location.city || "",
          country: location.country || "",
        };
        baseData.allowMemberLocationOverride = allowMemberLocationOverride;
      }
      const groupDocRef = await firestore()
        .collection("groups")
        .add(baseData);

      const userDocRef = firestore().collection("users").doc(user.uid);
      await userDocRef.update({
        groupIds: firestore.FieldValue.arrayUnion(groupDocRef.id),
      });

      if (groupImgUri) {
        const response = await fetch(groupImgUri);
        const blob = await response.blob();
        const refPath = `group-pictures/${groupDocRef.id}/original.jpeg`;
        const storageReference = storage().ref(refPath);
        await storageReference.put(blob);
      }

      Alert.alert(t("SUCCESS"), t("GROUP_CREATED_SUCCESSFULLY"));
      router.replace({
        pathname: "/(tabs)/groups",
        params: {
          newGroupImage: groupImgUri,
          newGroupId: groupDocRef.id,
        },
      });
    } catch (error) {
      console.error(t("ERROR"), error);
      Alert.alert(t("ERROR"), t("FAILED_TO_CREATE_GROUP"));
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePicChange = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: "images",
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });

    if (!result.canceled) {
      setUploadingImg(true);
      try {
        const fileUri = result.assets[0].uri;
        setGroupImgUri(fileUri);
      } catch (error) {
        console.error("Error uploading image: ", error);
        Alert.alert(t("ERROR"), t("FAILED_TO_UPLOAD_IMAGE"));
      } finally {
        setUploadingImg(false);
      }
    }
  };

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      <ThemedView style={styles.container}>
        <ThemedView style={styles.profilePicContainer}>
          <TouchableOpacity
            onPress={handleProfilePicChange}
            disabled={uploadingImg || loading}
          >
            <Image
              source={groupImgUri ? { uri: groupImgUri } : DefaultGroupImage}
              style={styles.profilePic}
              placeholder={DefaultGroupImage}
              cachePolicy="disk"
              transition={300}
            />
          </TouchableOpacity>
        </ThemedView>
        <ThemedText style={styles.label}>{t("GROUP_NAME")}</ThemedText>
        <TextInput
          style={styles.input}
          placeholder={t("ENTER_GROUP_NAME")}
          placeholderTextColor={iconColor}
          value={name}
          onChangeText={setName}
        />
        <ThemedText style={styles.label}>{`${t("DESCRIPTION")} (${t(
          "OPTIONAL"
        )})`}</ThemedText>
        <TextInput
          style={[styles.input, styles.textArea, styles.textAreaWithCounter]}
          placeholder={t("ENTER_GROUP_DESCRIPTION")}
          placeholderTextColor={iconColor}
          value={description}
          onChangeText={setDescription}
          multiline
          maxLength={500}
        />
        <ThemedText style={styles.counter}>
          {`${description.length}/500`}
        </ThemedText>
        <View style={styles.toggleRow}>
          <View style={styles.toggleTextGroup}>
            <ThemedText style={styles.toggleTitle}>
              {t("PUBLIC_GROUP")}
            </ThemedText>
            <ThemedText style={styles.toggleHint}>
              {t("PUBLIC_GROUP_HELP")}
            </ThemedText>
          </View>
          <Switch value={isPublic} onValueChange={setIsPublic} />
        </View>
        <LocationPicker
          value={location}
          onChange={setLocation}
          label={t("GROUP_LOCATION_OPTIONAL")}
        />
        {location && (
          <View style={styles.toggleRow}>
            <ThemedText style={styles.toggleLabel}>
              {t("ALLOW_MEMBER_LOCATION_OVERRIDE")}
            </ThemedText>
            <Switch
              value={allowMemberLocationOverride}
              onValueChange={setAllowMemberLocationOverride}
            />
          </View>
        )}
        {loading ? (
          <ActivityIndicator />
        ) : (
          <Button text={t("CREATE_GROUP")} onPress={handleCreateGroup} />
        )}
      </ThemedView>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollContent: { flexGrow: 1 },
  container: {
    flex: 1,
    padding: 20,
  },
  profilePicContainer: {},
  profilePic: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignSelf: "center",
    marginBottom: 20,
    backgroundColor: "#e0e0e0",
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
  },
  input: {
    width: "100%",
    height: 48,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 16,
    fontSize: 16,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  textArea: {
    height: 100,
    textAlignVertical: "top",
    paddingTop: 12,
  },
  textAreaWithCounter: {
    marginBottom: 4,
  },
  counter: {
    fontSize: 12,
    opacity: 0.6,
    textAlign: "right",
    marginBottom: 16,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  toggleTextGroup: { flex: 1, marginRight: 12 },
  toggleLabel: { flex: 1, fontSize: 14, marginRight: 12 },
  toggleTitle: { fontSize: 14 },
  toggleHint: { fontSize: 12, opacity: 0.7, marginTop: 2 },
});
